import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import QRCode from "qrcode";
import { Check, Copy, Download, KeyRound } from "lucide-react";
import { regenerateBackupCodes, startTotpSetup, verifyTotpSetup } from "../../api/security.js";
import type { TotpSetup } from "../../api/security.js";
import { ApiClientError } from "../../api/http.js";
import { secretClipboard } from "../../app/clipboard.js";
import { isCompleteOtp, normalizeOtp } from "../../lib/otp.js";
import { Dialog } from "../../ui/Dialog.js";
import { OtpInput } from "../../ui/forms.js";
import { Alert, Button } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

type Step = "scan" | "verify" | "codes";

interface TotpWizardProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onDone: () => void;
}

export function TotpWizard(props: TotpWizardProps): ReactNode {
  return (
    <Dialog open={props.open} onClose={props.onClose} title="Authenticator-App einrichten" description="Schritt für Schritt – ungefähr eine Minute." variant="center" size="md">
      {props.open ? <WizardBody onClose={props.onClose} onDone={props.onDone} /> : null}
    </Dialog>
  );
}

function WizardBody(props: { readonly onClose: () => void; readonly onDone: () => void }): ReactNode {
  const toast = useToast();
  const [step, setStep] = useState<Step>("scan");
  const [setup, setSetup] = useState<TotpSetup | undefined>(undefined);
  const [qr, setQr] = useState<string | undefined>(undefined);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect((): (() => void) => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const started = await startTotpSetup();
        const svg = await QRCode.toString(started.otpauthUrl, {
          type: "svg",
          margin: 1,
          color: { dark: "#03161B", light: "#E9F4F1" }
        });
        if (!cancelled) {
          setSetup(started);
          setQr(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
        }
      } catch {
        if (!cancelled) {
          setError("Die Einrichtung konnte nicht gestartet werden. Bitte erneut versuchen.");
        }
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, []);

  async function verify(value: string): Promise<void> {
    if (busy || !isCompleteOtp(value)) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await verifyTotpSetup(value);
      const fresh = await regenerateBackupCodes();
      setCodes(fresh);
      setStep("codes");
    } catch (err: unknown) {
      setError(err instanceof ApiClientError && err.status === 401 ? "Der Code passt nicht. Prüfe die Uhrzeit deines Geräts und versuche es erneut." : "Bestätigung fehlgeschlagen.");
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function copyCodes(): Promise<void> {
    const result = await secretClipboard.copy(codes.join("\n"));
    toast.notify(result === "copied" ? "Backup-Codes kopiert." : "Kopieren nicht möglich.", { tone: result === "copied" ? "success" : "danger" });
  }

  function downloadCodes(): void {
    const blob = new Blob([`PasswordFlow Backup-Codes\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "passwordflow-backup-codes.txt";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  if (step === "codes") {
    return (
      <div className="flex flex-col gap-5 p-5">
        <Alert tone="success">Zwei-Faktor-Schutz ist aktiv. Speichere die Backup-Codes jetzt – sie werden nur einmal angezeigt.</Alert>
        <ul className="grid grid-cols-2 gap-2 rounded-lg border border-line bg-ink p-4 font-mono text-sm text-foam">
          {codes.map((value: string) => (
            <li key={value} className="px-2 py-1 tracking-wider">
              {value}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" icon={Copy} onClick={(): void => void copyCodes()}>
            Kopieren
          </Button>
          <Button variant="secondary" icon={Download} onClick={downloadCodes}>
            Als Datei speichern
          </Button>
        </div>
        <Button icon={Check} className="w-full" onClick={props.onDone}>
          Fertig
        </Button>
      </div>
    );
  }

  if (step === "verify") {
    return (
      <div className="flex flex-col gap-5 p-5">
        <div>
          <h3 className="text-base font-semibold text-foam">Code bestätigen</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-mist">Gib den aktuellen sechsstelligen Code aus deiner App ein.</p>
        </div>
        <OtpInput
          label="Sechsstelliger Code"
          value={code}
          autoFocus
          disabled={busy}
          invalid={error !== undefined}
          onChange={(value: string): void => {
            setCode(normalizeOtp(value));
            setError(undefined);
          }}
          onComplete={(value: string): void => void verify(value)}
        />
        {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
        <div className="flex gap-3">
          <Button variant="ghost" onClick={(): void => setStep("scan")}>
            Zurück
          </Button>
          <Button className="flex-1" loading={busy} disabled={!isCompleteOtp(code)} onClick={(): void => void verify(code)}>
            Bestätigen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-5">
      <div className="grid gap-6 sm:grid-cols-[auto_1fr] sm:items-center">
        <div className="grid size-44 place-items-center rounded-lg border border-line bg-foam p-2">
          {qr !== undefined ? <img src={qr} alt="QR-Code für die Authenticator-App" className="size-full" /> : <span className="text-sm text-mist">Lädt …</span>}
        </div>
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-mist">Scanne den Code mit Aegis, Google Authenticator, 1Password oder einer anderen TOTP-App.</p>
          {setup !== undefined ? (
            <div className="rounded-lg border border-line bg-ink px-4 py-3">
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-mist-2">Oder manuell eingeben</p>
              <p className="mt-1 break-all font-mono text-sm tracking-wider text-foam">{setup.manualKey}</p>
            </div>
          ) : null}
        </div>
      </div>
      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
      <div className="flex gap-3">
        <Button variant="ghost" onClick={props.onClose}>
          Abbrechen
        </Button>
        <Button icon={KeyRound} className="flex-1" disabled={setup === undefined} onClick={(): void => setStep("verify")}>
          Ich habe den Code hinzugefügt
        </Button>
      </div>
    </div>
  );
}
